import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Spinner } from '@heroui/react';
import { PosterWithRef, type PosterConfig } from '../../../../../../packages/ui/src/components/extra/poster';
import { NavBar } from '@/components/layout';
import { useAppSelector } from '@/stores/hooks';
import { IconChevronLeft as ChevronLeft, IconChevronRight as ChevronRight } from '@tabler/icons-react';
import defaultAvatarUrl from '@/assets/images/default-avatar.webp';

const POSTER_DESIGN_WIDTH = 1080;
const POSTER_DEFAULT_PIXEL_HEIGHT = 1920;

const POSTER_CONFIG = {
  backgroundColor: '#FFFFFF',
  signatureHeight: 200,
  padding: 36,
  elementSpacing: 24,
  nicknameFontSize: 36,
  nicknameColor: '#000000',
  spaceNameFontSize: 24,
  spaceNameColor: '#000000',
  lineSpacing: 12,
  borderRadius: 6,
  qrcodePadding: 5,
} as const;

/** 从 poster.size 读取尺寸，数据缺失时使用默认值 */
function resolvePosterSourceSize(
  storedWidth: unknown,
  storedHeight: unknown,
): { sourceWidth: number; sourceHeight: number } {
  const w = typeof storedWidth === 'number' && storedWidth >= 1 ? Math.round(storedWidth) : 0;
  const h = typeof storedHeight === 'number' && storedHeight >= 1 ? Math.round(storedHeight) : 0;
  if (w && h) return { sourceWidth: w, sourceHeight: h };
  return { sourceWidth: POSTER_DESIGN_WIDTH, sourceHeight: POSTER_DEFAULT_PIXEL_HEIGHT };
}

/**
 * 活动海报页面
 * 
 * 路由模式：/activities/:id/posters
 */
export default function ActivityPostersPage() {
  const { id: activityId } = useParams<{ id: string }>();
  const user = useAppSelector((state) => state.auth.user);
  const userId = user?.id;
  const avatarUrl = user?.avatarUrl;
  const nickname = user?.nickname || '';
  
  const [current, setCurrent] = useState(0);
  const [posterDataUrls, setPosterDataUrls] = useState<Record<number, string>>({})

  /** 记录当前幻灯片 index，用于丢弃切换后晚到的 onSuccess */
  const currentSlideRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchEndY = useRef<number>(0);

  // 查询活动详情
  const { data: activity, isLoading } = trpc.activities.get.useQuery(
    { id: activityId! },
    {
      enabled: !!activityId,
    }
  );

  // 获取空间名称
  const spaceName = useMemo(() => {
    if (user?.spaces && user.spaces.length > 0) {
      return user.spaces[0].name || '';
    }
    return '';
  }, [user?.spaces]);

  const posterConfigs = useMemo(() => {
    if (!activity?.config?.posters || !Array.isArray(activity.config.posters)) {
      return [];
    }

    const mobileUrl = import.meta.env.VITE_MOBILE_URL || window.location.origin;

    return activity.config.posters.map((poster: any, index: number) => {
      const { sourceWidth, sourceHeight } = resolvePosterSourceSize(
        poster.size?.width,
        poster.size?.height,
      );

      const imageWidthScale = POSTER_DESIGN_WIDTH / sourceWidth;
      const renderedSourceHeight = Math.round(sourceHeight * imageWidthScale);

      const signatureHeight = POSTER_CONFIG.signatureHeight;
      const padding = POSTER_CONFIG.padding;
      const elementSpacing = POSTER_CONFIG.elementSpacing;
      const nicknameFontSize = POSTER_CONFIG.nicknameFontSize;
      const spaceNameFontSize = POSTER_CONFIG.spaceNameFontSize;
      const lineSpacing = POSTER_CONFIG.lineSpacing;
      const borderRadius = POSTER_CONFIG.borderRadius;
      const qrcodePadding = POSTER_CONFIG.qrcodePadding;

      const posterWidth = POSTER_DESIGN_WIDTH;
      const posterHeight = renderedSourceHeight + signatureHeight;

      const avatarSize = signatureHeight - padding * 2;
      const avatarLeft = padding;
      const avatarTop = renderedSourceHeight + padding;

      const nicknameLeft = padding + avatarSize + elementSpacing;
      const nicknameTop = renderedSourceHeight + signatureHeight / 2 - lineSpacing / 2 - nicknameFontSize;

      const spaceNameLeft = padding + avatarSize + elementSpacing;
      const spaceNameTop = renderedSourceHeight + signatureHeight / 2 + lineSpacing / 2;

      const qrcodeSize = signatureHeight - padding * 2;
      const qrcodeLeft = posterWidth - padding - qrcodeSize;
      const qrcodeTop = renderedSourceHeight + padding;

      const onlineLink = activity.onlineLink || '';
      const path = onlineLink.startsWith('/') ? onlineLink : `/${onlineLink}`;
      const qrcodeUrl = `${mobileUrl}${path}?activity=${activityId}&inviter=${userId}`;

      const config: PosterConfig = {
        width: posterWidth,
        height: posterHeight,
        backgroundColor: POSTER_CONFIG.backgroundColor,
        debug: false,
        // 提升到 2，显著提高移动端清晰度（之前是 1）
        pixelRatio: 2,
        views: [
          // 主图 - 支持任意高度
          {
            type: 'image',
            url: poster.url,
            top: 0,
            left: 0,
            width: posterWidth,
            height: renderedSourceHeight,
            fillMode: 'cover',
          },
          // 头像
          {
            type: 'image',
            url: avatarUrl || defaultAvatarUrl,
            top: avatarTop,
            left: avatarLeft,
            width: avatarSize,
            height: avatarSize,
            borderRadius: avatarSize,
            fillMode: 'cover',
          },
          // 昵称
          {
            type: 'text',
            content: nickname || '用户',
            top: nicknameTop,
            left: nicknameLeft,
            fontSize: nicknameFontSize,
            color: POSTER_CONFIG.nicknameColor,
            textAlign: 'left',
            baseLine: 'top', // 明确基线，避免文字位置偏移
          },
          // 空间名称
          {
            type: 'text',
            content: spaceName || '人文派',
            top: spaceNameTop,
            left: spaceNameLeft,
            fontSize: spaceNameFontSize,
            color: POSTER_CONFIG.spaceNameColor,
            textAlign: 'left',
            baseLine: 'top',
          },
          // 二维码白色背景（圆角）
          {
            type: 'rect',
            x: qrcodeLeft,
            y: qrcodeTop,
            width: qrcodeSize,
            height: qrcodeSize,
            backgroundColor: '#FFFFFF',
            borderRadius,
          },
          // 二维码
          {
            type: 'qrcode',
            text: qrcodeUrl,
            top: qrcodeTop,
            left: qrcodeLeft,
            width: qrcodeSize,
            height: qrcodeSize,
            background: '#FFFFFF',
            foreground: '#000000',
            padding: qrcodePadding,
          },
        ],
      };

      return {
        config,
        name: poster.name || `海报${index + 1}`,
        layoutKey: `${posterWidth}x${posterHeight}`,
      };
    });
  }, [
    activity,
    activityId,
    userId,
    avatarUrl,
    nickname,
    spaceName,
  ]);

  // 处理左右滑动翻页
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const deltaX = touchStartX.current - touchEndX.current;
    const deltaY = touchStartY.current - touchEndY.current;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        setCurrent((prev) => Math.min(prev + 1, posterConfigs.length - 1));
      } else {
        setCurrent((prev) => Math.max(prev - 1, 0));
      }
    }
    
    touchStartX.current = 0;
    touchStartY.current = 0;
    touchEndX.current = 0;
    touchEndY.current = 0;
  };

  const goToPrev = () => setCurrent((prev) => Math.max(prev - 1, 0));
  const goToNext = () => setCurrent((prev) => Math.min(prev + 1, posterConfigs.length - 1));

  const activePoster = posterConfigs[current];
  const activeConfig = activePoster?.config;

  useEffect(() => {
    currentSlideRef.current = current;
  }, [current]);

  const currentOverlay = posterDataUrls[current];
  const showSaveOverlay = !!currentOverlay;

  const handlePosterSuccess = useCallback((base64: string) => {
    const slide = current;
    setPosterDataUrls((prev) => {
      if (currentSlideRef.current !== slide) return prev;
      return { ...prev, [slide]: base64 };
    });
  }, [current]);

  if (isLoading) {
    return (
      <>
        <NavBar title="活动海报" showBack />
        <div className="flex items-center justify-center min-h-[60vh] pt-[var(--navbar-height,44px)]">
          <Spinner className="w-8 h-8" />
        </div>
      </>
    );
  }

  if (!activity || !posterConfigs.length) {
    return (
      <>
        <NavBar title="活动海报" showBack />
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 pt-[var(--navbar-height,44px)]">
          <p className="text-default-500">暂无海报</p>
          {process.env.NODE_ENV === 'development' && (
            <p className="text-xs text-default-500 mt-2">activity.config.posters: {JSON.stringify((activity?.config as any)?.posters?.length || 0)}</p>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <NavBar
        title="活动海报"
        showBack
      />
      <div className="flex flex-col w-full min-h-[100dvh] pt-[var(--navbar-height,44px)] bg-background">
        {/* 图片展示区 - flex-1 支持滚动 */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-y-auto relative select-none"
          style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex items-start justify-center p-4">
            <div className="relative w-full max-w-full">
              {activeConfig && (
                <div className="relative">
                  <PosterWithRef
                    key={`poster-${current}`}
                    config={activeConfig}
                    className="w-full relative z-0"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxWidth: '100%',
                      display: 'block',
                    }}
                    onSuccess={handlePosterSuccess}
                    onError={(error) => {
                      console.error('海报生成失败:', error);
                      console.error('海报配置:', activeConfig);
                    }}
                  />

                  {/* 使用稳定的 img 覆盖 canvas，提升微信长按保存体验 */}
                  {showSaveOverlay && (
                    <img
                      src={currentOverlay}
                      alt="活动海报"
                      className="absolute top-0 left-0 z-10 w-full h-auto block"
                      style={{
                        pointerEvents: 'auto',
                        WebkitTouchCallout: 'default',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                      }}
                      draggable={false}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 左右切换按钮 - fixed 布局，固定在视口 */}
        {posterConfigs.length > 1 && (
          <>
            {current > 0 && (
              <button
                onClick={goToPrev}
                className="fixed left-4 top-1/2 -translate-y-1/2 z-30 bg-background/90 hover:bg-background rounded-full p-3 shadow-lg transition-all active:scale-95"
                aria-label="上一张"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            {current < posterConfigs.length - 1 && (
              <button
                onClick={goToNext}
                className="fixed right-4 top-1/2 -translate-y-1/2 z-30 bg-background/90 hover:bg-background rounded-full p-3 shadow-lg transition-all active:scale-95"
                aria-label="下一张"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </>
        )}

        {/* 底部信息区 */}
        <div className="border-t border-border bg-background p-4">
          {/* 指示器 */}
          <div className="flex justify-center gap-2 mb-3">
            {posterConfigs.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === current ? 'bg-primary scale-125' : 'bg-default-300'
                }`}
              />
            ))}
          </div>

          {/* 图片名称 */}
          <div className="text-center text-sm font-medium mb-1">
            {activePoster?.name || '海报'}
          </div>

          {/* 图片数量及提示 */}
          <div className="text-center text-xs text-default-500">
            共{posterConfigs.length}张海报 · 长按图片保存到相册
          </div>
        </div>
      </div>
    </>
  );
}
